import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Terminal } from "xterm";
import "xterm/css/xterm.css";

import Loading from "../../../components/common/loading";
import NotificationManager from "../../../utils/notificationManager";
import "./deviceOpsExperience.css";

const INACTIVITY_TIMEOUT_MESSAGE =
  "Session ended after 5 minutes of inactivity.";
const BROWSER_RECONNECT_DELAY_MS = 750;
const MAX_BROWSER_RECONNECT_ATTEMPTS = 6;

const websocketStatusLabel = (status) => {
  switch (status) {
    case "connecting":
      return "Connecting";
    case "connected":
      return "Connected";
    case "waiting_for_device":
      return "Waiting Device";
    case "disconnected":
      return "Disconnected";
    case "error":
      return "Error";
    default:
      return "Idle";
  }
};

const decodeFrame = (frame, decoder) => {
  if (typeof frame === "string") {
    return frame;
  }

  if (frame instanceof ArrayBuffer) {
    return decoder.decode(new Uint8Array(frame));
  }

  if (frame instanceof Blob) {
    return null;
  }

  return null;
};

const getTerminalTheme = () => {
  return {
    background: "#131c27",
    foreground: "#edf3fb",
    cursor: "#7dd3fc",
    cursorAccent: "#131c27",
    selectionBackground: "rgba(125, 211, 252, 0.18)",
    black: "#131c27",
    brightBlack: "#4f6276",
  };
};

const resolveBrowserSocketUrl = (browserWebSocketUrl) => {
  if (!browserWebSocketUrl) {
    return "";
  }

  if (/^(wss?:|https?:)\/\//i.test(browserWebSocketUrl)) {
    return browserWebSocketUrl;
  }

  try {
    const parsedUrl = new URL(browserWebSocketUrl, window.location.href);
    const currentProtocol =
      window.location.protocol === "https:" ? "wss:" : "ws:";

    parsedUrl.protocol = currentProtocol;

    return parsedUrl.toString();
  } catch (_error) {
    return browserWebSocketUrl;
  }
};

export default function DeviceSerialConsole({
  match,
  user,
  deviceOffline,
  offlineReason,
  shellState,
  hideOfflineCallout = false,
}) {
  const serial = match?.params?.enclosureSerialNumber;
  const isAdmin = Boolean(user?.is_enterprise_admin || user?.has_admin_privileges);
  const compatibilityPending = shellState?.isCompatibilityKnown === false;
  const blockedByOffline = Boolean(deviceOffline);
  const blockedByApiVersion =
    shellState?.isCompatibilityKnown && shellState?.isCompatible === false;
  const blocked =
    !isAdmin || compatibilityPending || blockedByOffline || blockedByApiVersion;

  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [wsState, setWsState] = useState("idle");
  const [statusMessage, setStatusMessage] = useState(
    "Start a shell session to connect to the device shell. Shell sessions automatically end after 5 minutes of inactivity.",
  );
  const [errorMessage, setErrorMessage] = useState("");

  const terminalHostRef = useRef(null);
  const terminalRef = useRef(null);
  const wsRef = useRef(null);
  const sessionRef = useRef("");
  const unmountingRef = useRef(false);
  const inactivityTimeoutNoticeRef = useRef(false);
  const decoderRef = useRef(new TextDecoder());
  const browserWebSocketUrlRef = useRef("");
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const manualStopRef = useRef(false);

  const authHeaders = useMemo(() => {
    return {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
  }, []);

  const writeLine = useCallback((line) => {
    if (!terminalRef.current) {
      return;
    }
    terminalRef.current.writeln(`\r\n${line}`);
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (!reconnectTimerRef.current) {
      return;
    }

    window.clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = null;
  }, []);

  const clearLocalSession = useCallback(() => {
    clearReconnectTimer();
    browserWebSocketUrlRef.current = "";
    reconnectAttemptsRef.current = 0;
    manualStopRef.current = false;
    sessionRef.current = "";
    setSessionId("");
    setStopping(false);
  }, [clearReconnectTimer]);

  const closeSocket = useCallback(({ intentional = true } = {}) => {
    clearReconnectTimer();
    const current = wsRef.current;
    if (!current) {
      return;
    }

    wsRef.current = null;
    current.__synIntentionalClose = intentional;
    try {
      if (
        current.readyState === WebSocket.OPEN ||
        current.readyState === WebSocket.CONNECTING
      ) {
        current.close();
      }
    } catch (_error) {
      // Ignore socket close failures.
    }
  }, [clearReconnectTimer]);

  const connectBrowserSocket = useCallback(
    (browserWebSocketUrl, { isReconnect = false } = {}) => {
      const resolvedBrowserWebSocketUrl =
        resolveBrowserSocketUrl(browserWebSocketUrl);

      if (!resolvedBrowserWebSocketUrl) {
        setWsState("error");
        setErrorMessage("Missing browser websocket URL.");
        return;
      }

      browserWebSocketUrlRef.current = resolvedBrowserWebSocketUrl;
      manualStopRef.current = false;
      closeSocket({ intentional: true });
      inactivityTimeoutNoticeRef.current = false;
      setWsState("connecting");
      setStatusMessage(
        isReconnect
          ? "Reconnecting terminal to shell session..."
          : "Connecting terminal to shell session...",
      );

      const ws = new WebSocket(resolvedBrowserWebSocketUrl);
      ws.binaryType = "arraybuffer";
      ws.__synIntentionalClose = false;
      wsRef.current = ws;

      ws.onopen = () => {
        if (wsRef.current !== ws) {
          return;
        }

        inactivityTimeoutNoticeRef.current = false;
        reconnectAttemptsRef.current = 0;
        setWsState("connected");
        setErrorMessage("");
        setStatusMessage(
          isReconnect
            ? "Shell reconnected. Waiting for device output."
            : "Shell connected. Waiting for device output.",
        );
        writeLine(isReconnect ? "[shell reconnected]" : "[shell connected]");
      };

      ws.onmessage = (event) => {
        if (!terminalRef.current) {
          return;
        }

        const decoded = decodeFrame(event.data, decoderRef.current);
        if (decoded === null) {
          return;
        }

        if (typeof decoded === "string" && decoded.trim().startsWith("{")) {
          try {
            const maybeStatus = JSON.parse(decoded);
            if (maybeStatus?.type === "status") {
              const normalized = maybeStatus.status || "connected";
              if (normalized === "waiting_for_device") {
                setWsState("waiting_for_device");
                setStatusMessage("Waiting for the device shell connection...");
              } else if (normalized === "device_connected") {
                setWsState("connected");
                setStatusMessage("Device shell connected.");
                writeLine("[shell ready]");
              } else if (normalized === "device_disconnected") {
                clearLocalSession();
                setWsState("disconnected");
                setStatusMessage("Device shell disconnected.");
                writeLine("[shell disconnected]");
              } else if (normalized === "connected") {
                setWsState("connected");
                setStatusMessage("Shell session active.");
              } else if (normalized === "inactivity_timeout") {
                const timeoutMessage =
                  typeof maybeStatus.message === "string" &&
                  maybeStatus.message.trim()
                    ? maybeStatus.message
                    : INACTIVITY_TIMEOUT_MESSAGE;
                inactivityTimeoutNoticeRef.current = true;
                clearLocalSession();
                setWsState("disconnected");
                setErrorMessage("");
                setStatusMessage(timeoutMessage);
                writeLine("[session ended due to inactivity]");
              }
              return;
            }
          } catch (_error) {
            // Non-status frames are rendered to terminal as plain text.
          }
        }

        terminalRef.current.write(decoded);
      };

      ws.onclose = () => {
        if (wsRef.current === ws) {
          wsRef.current = null;
        }

        if (unmountingRef.current) {
          return;
        }

        const canRetry =
          !ws.__synIntentionalClose &&
          !manualStopRef.current &&
          Boolean(sessionRef.current) &&
          Boolean(browserWebSocketUrlRef.current) &&
          reconnectAttemptsRef.current < MAX_BROWSER_RECONNECT_ATTEMPTS;

        if (canRetry) {
          reconnectAttemptsRef.current += 1;
          const attemptNumber = reconnectAttemptsRef.current;
          clearReconnectTimer();
          setWsState("connecting");
          setStatusMessage(
            `Shell connection dropped. Reconnecting (${attemptNumber}/${MAX_BROWSER_RECONNECT_ATTEMPTS})...`,
          );
          writeLine("[connection lost, retrying]");
          reconnectTimerRef.current = window.setTimeout(() => {
            reconnectTimerRef.current = null;
            const nextUrl = browserWebSocketUrlRef.current;
            if (
              !nextUrl ||
              !sessionRef.current ||
              unmountingRef.current ||
              manualStopRef.current
            ) {
              return;
            }

            connectBrowserSocket(nextUrl, { isReconnect: true });
          }, BROWSER_RECONNECT_DELAY_MS);
          return;
        }

        clearLocalSession();
        setWsState("disconnected");
        if (!inactivityTimeoutNoticeRef.current) {
          setStatusMessage("Shell session disconnected.");
          writeLine("[session disconnected]");
        }
      };

      ws.onerror = () => {
        if (unmountingRef.current) {
          return;
        }

        setWsState("error");
        setStatusMessage("Browser websocket failed to connect.");
        setErrorMessage(
          "Connection error while opening shell session. Check websocket reachability and browser mixed-content blocking.",
        );
      };
    },
    [clearLocalSession, clearReconnectTimer, closeSocket, writeLine],
  );

  const endSession = useCallback(
    async ({ silent = false } = {}) => {
      const activeSessionId = sessionRef.current;
      if (!activeSessionId || !serial) {
        closeSocket({ intentional: true });
        if (!silent) {
          setStopping(false);
        }
        return;
      }

      manualStopRef.current = true;
      if (!silent) {
        setStopping(true);
      }

      try {
        await axios.delete(
          `/v1/devices/${encodeURIComponent(serial)}/cli/session/${encodeURIComponent(activeSessionId)}`,
          {
            headers: authHeaders,
          },
        );

        if (!silent) {
          NotificationManager.success("Shell session ended.");
        }
      } catch (error) {
        if (!silent) {
          const message =
            error?.response?.data?.message ||
            "Failed to end shell session.";
          NotificationManager.alert(message);
          setErrorMessage(message);
        }
      } finally {
        closeSocket({ intentional: true });
        inactivityTimeoutNoticeRef.current = false;
        sessionRef.current = "";
        browserWebSocketUrlRef.current = "";
        reconnectAttemptsRef.current = 0;
        manualStopRef.current = false;
        if (!silent) {
          setStopping(false);
        }
        if (!unmountingRef.current) {
          setSessionId("");
          setWsState("idle");
          setStatusMessage(
            "Start a shell session to connect to the device shell.",
          );
        }
      }
    },
    [authHeaders, closeSocket, serial],
  );

  const startSession = useCallback(async () => {
    if (!serial || blocked || starting || stopping) {
      return;
    }

    manualStopRef.current = false;
    setStarting(true);
    setErrorMessage("");
    inactivityTimeoutNoticeRef.current = false;

    try {
      const response = await axios.post(
        `/v1/devices/${encodeURIComponent(serial)}/cli/session`,
        {},
        {
          headers: authHeaders,
        },
      );

      const payload = response?.data?.data || {};
      if (!payload.sessionId || !payload.browserWebSocketUrl) {
        throw new Error("Missing session details in API response.");
      }

      sessionRef.current = payload.sessionId;
      setSessionId(payload.sessionId);
      setStatusMessage("Shell session started. Opening connection...");
      connectBrowserSocket(payload.browserWebSocketUrl, { isReconnect: false });
      NotificationManager.success("Shell session started.");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to start shell session.";
      setErrorMessage(message);
      setStatusMessage("Unable to start shell session.");
      NotificationManager.alert(message);
    } finally {
      setStarting(false);
    }
  }, [authHeaders, blocked, connectBrowserSocket, serial, starting, stopping]);

  useEffect(() => {
    if (!terminalHostRef.current) {
      return;
    }

    const term = new Terminal({
      cursorBlink: true,
      convertEol: true,
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize: 13,
      lineHeight: 1.2,
      letterSpacing: 0.15,
      theme: getTerminalTheme(),
      scrollback: 5000,
      rows: 26,
      cols: 120,
    });

    term.open(terminalHostRef.current);
    term.writeln("Synaccess Shell");
    term.writeln("");
    term.writeln("Use Connect to start.");

    terminalRef.current = term;
    const onDataDisposable = term.onData((input) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        return;
      }

      ws.send(input);
    });

    return () => {
      onDataDisposable.dispose();
      terminalRef.current = null;
      term.dispose();
    };
  }, []);

  useEffect(() => {
    unmountingRef.current = false;
    return () => {
      unmountingRef.current = true;

      const pendingSessionId = sessionRef.current;
      manualStopRef.current = true;
      if (pendingSessionId && serial) {
        axios
          .delete(
            `/v1/devices/${encodeURIComponent(serial)}/cli/session/${encodeURIComponent(pendingSessionId)}`,
            { headers: authHeaders },
          )
          .catch(() => {
            // Best effort auto-stop on page leave.
          });
      }

      closeSocket({ intentional: true });
      sessionRef.current = "";
    };
  }, [authHeaders, closeSocket, serial]);

  const hasActiveSession = Boolean(sessionId);
  const terminalStateLabel = compatibilityPending
    ? "Checking"
    : hasActiveSession
      ? "Live Session"
      : "Ready";
  const actionLabel = starting
    ? "Connecting..."
    : stopping
      ? "Disconnecting..."
      : hasActiveSession
        ? "Disconnect"
        : "Connect";
  const actionDisabled = starting || stopping || (!hasActiveSession && blocked);
  const statusBadgeClass =
    wsState === "connected"
      ? "device-ops-badge device-ops-badge--accent"
      : wsState === "error" || wsState === "disconnected"
        ? "device-ops-badge device-ops-badge--muted"
        : "device-ops-badge";
  const resolvedStatusMessage = compatibilityPending
    ? "Checking shell compatibility for this device..."
    : blockedByApiVersion
      ? ""
      : !isAdmin
        ? "Enterprise administrator access is required to start a shell session."
        : blockedByOffline
          ? offlineReason ||
            "Device is offline. Shell session cannot be started until the device reconnects."
          : statusMessage;

  return (
    <div className="device-ops-shell-layout">
      <div className="device-ops-shell-controls">
        <div className="device-ops-card-heading">
          <h5 className="device-ops-card-title">Shell Session</h5>

          <div className="device-ops-header-note-list">
            <p className="device-ops-header-note">
              Shell command reference:{" "}
              <a
                href="https://synaccess-pdus.readme.io/reference/commands"
                target="_blank"
                rel="noopener noreferrer"
                className="device-ops-link"
              >
                Synaccess CLI command documentation
              </a>
              .
            </p>
            <p className="device-ops-header-note">
              Run <code>help</code> in the shell to see command examples and
              available commands.
            </p>
          </div>
        </div>
        <div className="device-ops-actions">
          <span className={statusBadgeClass}>{websocketStatusLabel(wsState)}</span>
          <button
            type="button"
            className={`device-ops-button device-ops-button--primary ${
              actionDisabled ? "device-ops-button--disabled" : ""
            }`}
            onClick={
              actionDisabled
                ? undefined
                : hasActiveSession
                  ? () => endSession()
                  : startSession
            }
            disabled={actionDisabled}
          >
            {starting || stopping ? <Loading black /> : null}
            {actionLabel}
          </button>
        </div>
      </div>

      {!isAdmin ? (
        <div className="device-ops-callout device-ops-callout--warning">
          This feature is restricted to enterprise administrators.
        </div>
      ) : null}

      {blockedByApiVersion ? (
        <div className="device-ops-callout device-ops-callout--warning">
          {shellState?.warningMessage ||
            "Shell is unavailable for this firmware/API version."}
        </div>
      ) : null}

      {blockedByOffline && !hideOfflineCallout ? (
        <div className="device-ops-callout device-ops-callout--warning">
          {offlineReason ||
            "Device is offline. Shell session cannot be started until the device reconnects."}
        </div>
      ) : null}

      {resolvedStatusMessage ? (
        <div className="device-ops-status-text">{resolvedStatusMessage}</div>
      ) : null}
      {errorMessage ? <div className="device-ops-error-text">{errorMessage}</div> : null}

      <div className="device-ops-terminal-frame">
        <div className="device-ops-terminal-chrome">
          <div className="device-ops-terminal-chrome-left">
            <span className="device-ops-terminal-label">DX Shell</span>
            {serial ? (
              <span className="device-ops-terminal-meta">SN {serial}</span>
            ) : null}
          </div>
          <span className="device-ops-terminal-session-state">
            {terminalStateLabel}
          </span>
        </div>
        <div className="device-ops-terminal-body">
          <div ref={terminalHostRef} className="device-ops-terminal-viewport" />
        </div>
      </div>
    </div>
  );
}
